import { useEffect, useState } from 'react'
import { useAdmin } from '../context/AdminContext'
import { createAdminApi } from '../services/adminApi'
import styles from './AdminPage.module.css'

function CollapsibleSection({ title, defaultOpen = true, disabled = false, children }: {
  title: string
  defaultOpen?: boolean
  disabled?: boolean
  children: React.ReactNode
}) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <div className={styles.section}>
      <div className={`${styles.sectionHeader} ${open ? styles.expanded : ''}`} onClick={() => setOpen(v => !v)}>
        <h2>{title}</h2>
        {disabled && <span className={styles.requiresAdmin}>requires admin key</span>}
        <span className={styles.arrow}>▶</span>
      </div>
      {open && (
        <div className={`${styles.sectionBody} ${disabled ? styles.sectionDisabled : ''}`}>
          {children}
        </div>
      )}
    </div>
  )
}

interface SaveStatus { msg: string; ok: boolean }

function useSaveStatus() {
  const [status, setStatus] = useState<SaveStatus | null>(null)
  function flash(msg: string, ok: boolean) {
    setStatus({ msg, ok })
    setTimeout(() => setStatus(null), 3000)
  }
  return { status, flash }
}


export default function AdminPage() {
  const { apiKey, isAuthenticated } = useAdmin()

  // Features
  const [featureEvallog, setFeatureEvallog] = useState(false)
  const featuresStatus = useSaveStatus()

  // Catchphrase settings
  const [similarityThreshold, setSimilarityThreshold] = useState<number>(0.85)
  const [thresholdDraft, setThresholdDraft] = useState<string>('')
  const [editingThreshold, setEditingThreshold] = useState(false)
  const [savingThreshold, setSavingThreshold] = useState(false)
  const thresholdStatus = useSaveStatus()

  // Application defaults
  const [defaultStory, setDefaultStory] = useState('')
  const [defaultCharacter, setDefaultCharacter] = useState('')
  const [availableStories, setAvailableStories] = useState<{ sourceFilename: string; title: string; inDatabase: boolean; characterName: string }[]>([])
  const [availableCharacters, setAvailableCharacters] = useState<{ name: string; version: number }[]>([])
  const appDefaultsStatus = useSaveStatus()

  const api = createAdminApi()

  // Load feature flags separately since they may be updated at runtime
  useEffect(() => {
    if (!apiKey) return
    api.getFeatures().then(flags => {
      setFeatureEvallog(!!flags['evallog'])
    }).catch(() => {})
  }, [apiKey]) // eslint-disable-line react-hooks/exhaustive-deps

  // Load catchphrase settings
  useEffect(() => {
    if (!apiKey) return
    api.getCachedResponsesSettings().then(s => {
      setSimilarityThreshold(s.similarity_threshold)
    }).catch(() => {})
  }, [apiKey]) // eslint-disable-line react-hooks/exhaustive-deps

  // Load application defaults + available stories/characters
  useEffect(() => {
    if (!apiKey) return
    api.getAppSettings().then(s => {
      setDefaultStory(s.defaultStory)
      setDefaultCharacter(s.defaultCharacter)
    }).catch(() => {})
    api.getStories()
      .then(all => setAvailableStories(all.filter(s => s.inDatabase)))
      .catch(() => {})
    api.getCharacters().then(setAvailableCharacters).catch(() => {})
  }, [apiKey]) // eslint-disable-line react-hooks/exhaustive-deps

  async function saveFeatures(features: Record<string, boolean>) {
    if (!features) {
      console.warn('No features provided to saveFeatures')
      return
    }

    try {
      await api.setFeatures(features)
      featuresStatus.flash('Saved', true)
    } catch (e) {
      featuresStatus.flash(String(e), false)
    }
  }

  async function saveAppDefaults() {
    try {
      const result = await api.setAppSettings({ defaultStory, defaultCharacter })
      setDefaultStory(result.defaultStory)
      setDefaultCharacter(result.defaultCharacter)
      appDefaultsStatus.flash('Saved', true)
    } catch (e) {
      appDefaultsStatus.flash(String(e), false)
    }
  }

  async function saveThreshold() {
    const v = parseFloat(thresholdDraft)
    if (isNaN(v) || v < 0 || v > 1) {
      thresholdStatus.flash('Must be between 0 and 1', false)
      return
    }
    setSavingThreshold(true)
    try {
      const result = await api.setCachedResponsesSettings(v)
      setSimilarityThreshold(result.similarity_threshold)
      setEditingThreshold(false)
      thresholdStatus.flash('Saved', true)
    } catch (e) {
      thresholdStatus.flash(String(e), false)
    } finally {
      setSavingThreshold(false)
    }
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <h1>Admin Config</h1>
      </header>

      <div className={styles.content}>
        <CollapsibleSection title="Feature Flags" disabled={!isAuthenticated}>
          <p className={styles.help}>Toggle runtime feature flags. Changes take effect immediately.</p>
          <label className={styles.featureLabel}>
            <input type="checkbox" checked={featureEvallog} onChange={() => { const next = !featureEvallog; setFeatureEvallog(next); saveFeatures({ evallog: next }) }} />
            Eval Logging — write LLM prompt/response pairs to JSONL and database
          </label>
          <div className={styles.sectionFooter}>
            {featuresStatus.status && <span className={`${styles.saveStatus} ${featuresStatus.status.ok ? styles.ok : styles.err}`}>{featuresStatus.status.msg}</span>}
          </div>
        </CollapsibleSection>

        <CollapsibleSection title="Application Defaults" disabled={!isAuthenticated}>
          <p className={styles.help}>Default story and character used when a session is started with no parameters.</p>
          <div className={styles.fieldRow}>
            <span className={styles.fieldLabel}>Default Story</span>
            <select
              value={defaultStory}
              onChange={e => setDefaultStory(e.target.value)}
              className={styles.selectInput}
              disabled={!isAuthenticated}
            >
              {!defaultStory && <option value="" disabled>(not set)</option>}
              {availableStories.map(s => (
                <option key={s.sourceFilename} value={s.sourceFilename}>
                  {s.title || s.sourceFilename}{s.characterName ? ` (${s.characterName})` : ''}
                </option>
              ))}
            </select>
          </div>
          <div className={styles.fieldRow}>
            <span className={styles.fieldLabel}>Default Character</span>
            <select
              value={defaultCharacter}
              onChange={e => setDefaultCharacter(e.target.value)}
              className={styles.selectInput}
              disabled={!isAuthenticated}
            >
              {!defaultCharacter && <option value="" disabled>(not set)</option>}
              {availableCharacters.map(c => (
                <option key={c.name} value={c.name}>{c.name} v{c.version}</option>
              ))}
            </select>
          </div>
          <div className={styles.sectionFooter}>
            <button className={styles.saveBtn} onClick={saveAppDefaults} disabled={!isAuthenticated}>Save</button>
            {appDefaultsStatus.status && appDefaultsStatus.status.msg && (
              <span className={`${styles.saveStatus} ${appDefaultsStatus.status.ok ? styles.ok : styles.err}`}>
                {appDefaultsStatus.status.msg}
              </span>
            )}
          </div>
        </CollapsibleSection>

        <CollapsibleSection title="Catchphrase Settings" disabled={!isAuthenticated}>
          <p className={styles.help}>Controls how closely a user message must match a catchphrase to trigger a cached response. Lower values match more loosely; higher values require a closer match.</p>
          <div className={styles.fieldRow}>
            <span className={styles.fieldLabel}>Similarity Threshold</span>
            {editingThreshold ? (
              <div className={styles.fieldEdit}>
                <input
                  type="number"
                  className={styles.numberInput}
                  min={0}
                  max={1}
                  step={0.01}
                  value={thresholdDraft}
                  onChange={e => setThresholdDraft(e.target.value)}
                  disabled={savingThreshold}
                  autoFocus
                />
                <button className={styles.saveBtn} onClick={saveThreshold} disabled={savingThreshold}>Save</button>
                <button className={styles.cancelBtn} onClick={() => { setEditingThreshold(false); thresholdStatus.flash('', true) }} disabled={savingThreshold}>Cancel</button>
              </div>
            ) : (
              <div className={styles.fieldEdit}>
                <span className={styles.fieldValue}>{similarityThreshold.toFixed(2)}</span>
                <button className={styles.editBtn} onClick={() => { setThresholdDraft(String(similarityThreshold)); setEditingThreshold(true) }}>Edit</button>
              </div>
            )}
          </div>
          <div className={styles.sectionFooter}>
            {thresholdStatus.status && thresholdStatus.status.msg && <span className={`${styles.saveStatus} ${thresholdStatus.status.ok ? styles.ok : styles.err}`}>{thresholdStatus.status.msg}</span>}
          </div>
        </CollapsibleSection>

      </div>
    </div>
  )
}
